import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Pin, Search, Star, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { FavouriteCode } from '../types/codes';

export interface CodeItem {
  code: string;
  description: string;
  tariffAmount?: number;
  category?: string;
}

interface CodeSelectorProps {
  /** 'icd10' or 'nhrpl' */
  codeType: 'icd10' | 'nhrpl';
  /** Currently selected codes */
  selected: CodeItem[];
  /** Callback when selection changes */
  onChange: (codes: CodeItem[]) => void;
  /** Client-side search function from the cache hook */
  searchFn: (query: string, limit?: number) => CodeItem[];
  /** Whether the cache is still loading */
  cacheLoading?: boolean;
  /** Label above the selector */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
}

export function CodeSelector({
  codeType,
  selected,
  onChange,
  searchFn,
  cacheLoading,
  label,
  placeholder = 'Search by code or description (e.g. N80, endometriosis)',
}: CodeSelectorProps) {
  const { get, post, del } = useApi();

  // Favourites
  const [favourites, setFavourites] = useState<FavouriteCode[]>([]);
  const [favsLoading, setFavsLoading] = useState(true);

  // Search
  const [query, setQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load favourites on mount (ICD-10 only)
  useEffect(() => {
    if (codeType !== 'icd10') {
      setFavsLoading(false);
      return;
    }
    get<FavouriteCode[]>('/icd10/favourites')
      .then(r => {
        if (r.success && r.data) setFavourites(r.data);
      })
      .finally(() => setFavsLoading(false));
  }, [get, codeType]);

  // Search results computed synchronously — no debounce needed for in-memory search
  const results = useMemo(() => {
    if (query.length < 1) return [];
    return searchFn(query, 30);
  }, [query, searchFn]);

  // Re-open dropdown whenever new results arrive
  useEffect(() => {
    if (results.length > 0) setDropdownOpen(true);
  }, [results]);

  const showResults = query.length >= 1 && dropdownOpen;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedCodes = useMemo(() => new Set(selected.map(s => s.code)), [selected]);

  const handleSelect = useCallback((item: CodeItem) => {
    if (selectedCodes.has(item.code)) return;
    onChange([...selected, item]);
    setQuery('');
    setDropdownOpen(false);
    inputRef.current?.focus();

    // Increment useCount if it's a favourite
    if (codeType === 'icd10' && favourites.some(f => f.code === item.code)) {
      post('/icd10/favourites/increment', { code: item.code }).then(r => {
        if (r.success && r.data) setFavourites(r.data as unknown as FavouriteCode[]);
      });
    }
  }, [selected, selectedCodes, onChange, codeType, favourites, post]);

  const handleRemove = useCallback((code: string) => {
    onChange(selected.filter(s => s.code !== code));
  }, [selected, onChange]);

  const handlePin = useCallback(async (item: CodeItem) => {
    if (codeType !== 'icd10') return;
    const isPinned = favourites.some(f => f.code === item.code);
    if (isPinned) {
      const r = await del<FavouriteCode[]>(`/icd10/favourites/${encodeURIComponent(item.code)}`);
      if (r.success && r.data) setFavourites(r.data);
    } else {
      const r = await post<FavouriteCode[]>('/icd10/favourites', { code: item.code, description: item.description });
      if (r.success && r.data) setFavourites(r.data);
    }
  }, [codeType, favourites, post, del]);

  const sortedFavourites = useMemo(
    () => [...favourites].sort((a, b) => (b.useCount || 0) - (a.useCount || 0)),
    [favourites]
  );

  return (
    <div className="space-y-3">
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map(item => (
            <span
              key={item.code}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-800"
            >
              <span className="font-mono text-xs font-bold">{item.code}</span>
              <span className="truncate max-w-[200px]">{item.description}</span>
              {item.tariffAmount != null && item.tariffAmount > 0 && (
                <span className="text-xs text-blue-600">R{item.tariffAmount.toFixed(0)}</span>
              )}
              <button
                onClick={() => handleRemove(item.code)}
                className="text-blue-400 hover:text-blue-700 flex-shrink-0"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Favourites grid (ICD-10 only) */}
      {codeType === 'icd10' && !favsLoading && sortedFavourites.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Star className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Favourites</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sortedFavourites.map(fav => (
              <button
                key={fav.code}
                onClick={() => handleSelect({ code: fav.code, description: fav.description })}
                disabled={selectedCodes.has(fav.code)}
                className={`px-2 py-1 text-xs rounded border transition-colors ${
                  selectedCodes.has(fav.code)
                    ? 'bg-blue-100 border-blue-300 text-blue-600 cursor-default'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                }`}
                title={fav.description}
              >
                <span className="font-mono font-bold">{fav.code}</span>
                {fav.useCount > 0 && (
                  <span className="ml-1 text-gray-400">({fav.useCount})</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search input */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setDropdownOpen(true); }}
            onFocus={() => setDropdownOpen(true)}
            placeholder={cacheLoading ? 'Loading codes...' : placeholder}
            disabled={cacheLoading}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          />
          {cacheLoading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-gray-400" />}
        </div>

        {/* Search results dropdown */}
        {showResults && results.length > 0 && (
          <ul className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
            {results.map(item => {
              const isSelected = selectedCodes.has(item.code);
              const isFav = favourites.some(f => f.code === item.code);

              return (
                <li key={item.code} className="flex items-center">
                  <button
                    onClick={() => handleSelect(item)}
                    disabled={isSelected}
                    className={`flex-1 text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    {isSelected ? (
                      <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    ) : (
                      <span className="w-4 flex-shrink-0" />
                    )}
                    <span className="font-mono font-bold text-xs">{item.code}</span>
                    <span className="text-gray-700 truncate">{item.description}</span>
                    {item.tariffAmount != null && item.tariffAmount > 0 && (
                      <span className="ml-auto text-xs text-gray-500 flex-shrink-0">R{item.tariffAmount.toFixed(0)}</span>
                    )}
                  </button>
                  {codeType === 'icd10' && (
                    <button
                      onClick={e => { e.stopPropagation(); handlePin(item); }}
                      className={`p-2 flex-shrink-0 ${isFav ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                      title={isFav ? 'Unpin favourite' : 'Pin as favourite'}
                    >
                      <Star className={`h-4 w-4 ${isFav ? 'fill-amber-500' : ''}`} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {showResults && query.length >= 1 && results.length === 0 && !cacheLoading && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg px-4 py-3 text-sm text-gray-500">
            No codes found for &ldquo;{query}&rdquo;
          </div>
        )}
      </div>
    </div>
  );
}
